import * as React from "react";
import type {
  AppContextMenuProps,
  AppContextMenuState,
  ExtendedPositioningProps,
} from "./AppContextualMenu.types";
import {
  useFocusFinders,
  useMenu_unstable,
  makeStyles,
} from "@fluentui/react-components";
import type { AppMenuItemProps } from "../AppMenuItem";
import { useMenuPositioning } from "../../hooks/useMenuPositioning";
import { AppContext } from "@1js/acui-context";
import {
  useFileMenuAsyncUIId,
  useShadowOverflowFixEnabled,
  useV9MenuAutoDismissFixEnabled,
  useDispatchOptimizationEnabled,
  useContextMenuAndFloatieRightClickDisabled,
} from "../../hooks/useNextComponentsEnabled";
import { performOnNextFrame } from "@1js/acui-common";
import type { AppMenuPopoverProps } from "../AppMenuPopover";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

const useHiddenMenuStyle = makeStyles({
  root: {
    visibility: "hidden",
    position: "absolute",
  },
});

export const useAppContextualMenu = (
  props: AppContextMenuProps
): AppContextMenuState => {
  const menuItems = props.items as AppMenuItemProps[];
  const appContext = React.useContext(AppContext);
  const isV9MenuAutoDismissFixEnabled = useV9MenuAutoDismissFixEnabled();
  const isDispatchOptimizationEnabled = useDispatchOptimizationEnabled();
  const isShadowOverflowFixEnabled = useShadowOverflowFixEnabled();
  const listRef = React.useRef<HTMLDivElement>(null);
  const popperPlacement =
    props.contextMenuRef?.current?.dataset?.popperPlacement;
  const menuPosition = !isShadowOverflowFixEnabled
    ? undefined
    : popperPlacement === "top-start" || popperPlacement === "top-end"
      ? "above"
      : "below";
  const isFileMenuAsyncUIIdEnabled = useFileMenuAsyncUIId();
  const isContextMenuAndFloatieRightClickDisabled =
    useContextMenuAndFloatieRightClickDisabled();

  const isAppFlyoutAnchorRestoreFocusFixEnabled = !!checkExperiment(
    appContext,
    ribbonExperiments.appFlyoutAnchorRestoreFocusFixEnabled
  );

  // Hook to find the first focusable element in the Menu.
  const { findFirstFocusable } = useFocusFinders();

  const state = useMenu_unstable({
    ...props,
    open: props.hidden ? false : true,
    closeOnScroll: false,
  }) as AppContextMenuState;

  const hiddenMenuStyle = useHiddenMenuStyle();

  if (menuItems?.findIndex((item) => item === undefined) >= 0) {
    appContext?.logger?.onMessage(
      `AppContextualMenu with id ${props.id} attempting to render an undefined item`
    );
  }

  const getElementTimingId = (
    id: string | undefined,
    interactivityTrackerContext: string | undefined
  ) => {
    if (
      isFileMenuAsyncUIIdEnabled &&
      id === "FileMenuFlyoutLauncherDropdown" &&
      interactivityTrackerContext === "FileMenuFlyoutLauncherDropdown"
    ) {
      return "file-menu";
    }

    return "context-menu";
  };

  React.useEffect(() => {
    if (
      !props.hidden &&
      listRef.current &&
      isDispatchOptimizationEnabled &&
      !props.positionAndHide
    ) {
      // In V9 menu, we move focus on render. Now that we do not unmount the menu, it will not move focus again.
      // Added a use effect that is applied when the hidden state changes to focus on the menu.
      performOnNextFrame(() => {
        const firstItem = findFirstFocusable(listRef.current!);
        firstItem?.focus();
      });
    }
  }, [
    props.hidden,
    findFirstFocusable,
    isDispatchOptimizationEnabled,
    props.positionAndHide,
  ]);

  return {
    ...props,
    ...state,
    appMenuItems: menuItems,
    appMenuPopoverProps: {
      id: props.id,
      ref: props.contextMenuRef
        ? (props.contextMenuRef as React.RefObject<HTMLDivElement>)
        : undefined,
      className: props.className,
      ...(isContextMenuAndFloatieRightClickDisabled && {
        onContextMenu: (event: React.MouseEvent) =>
          !props.openOnContext && event.preventDefault(),
      }),
      ...(isAppFlyoutAnchorRestoreFocusFixEnabled && {
        // We need to stop propagation of the keydown event to prevent other hidden menus from listening to the event.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        onKeyDown: React.useCallback<
          NonNullable<AppMenuPopoverProps["onKeyDown"]>
        >((ev) => {
          ev.stopPropagation();
        }, []),
      }),
    },
    appMenuListProps: {},
    positioning: {
      positioningRef: useMenuPositioning(props),
      unstable_disableTether: "all",
    } as unknown as ExtendedPositioningProps,
    preventDismissOnTargetClick: true,
    listRef,
    findFirstFocusable,
    isV9MenuAutoDismissFixEnabled,
    hiddenMenuStyle,
    isDispatchOptimizationEnabled,
    menuPosition,
    id: getElementTimingId(props.id, props.interactivityTrackerContext),
  };
};
